import React, { useState } from 'react';

import {
  DetectionIcon,
  FileIcon,
  UsersIcon,
  TechHardDriveIcon,
  InfoCircleIcon,
} from '@vlabs/icons';
import { Menu, MenuItem } from '@vlabs/pages/handlers/components';
import {
  BiometricIdentificationForm,
  TemplateRegistrationForm,
  SaveUniqueForm,
  SaveIdentifiedForm,
  DefineAttributesForm,
  CreatedHandlerForm,
  BiometricIdentificationFaceQualityForm,
} from '@vlabs/pages/handlers/preconfigured-scenarios/index';
import { routes } from '@vlabs/shared/navigation/routes';
import { selectAppServices, selectIsAppFeatureAvailable } from '@vlabs/shared/selectors/appSelectors';
import { selectListOptions } from '@vlabs/shared/selectors/listSelectors';
import { Modal, Tooltip } from '@vlabs/uikit';
import cn from 'classnames';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { connect } from 'react-redux';
import { generatePath, useHistory } from 'react-router-dom';

import { createPreconfiguredHandler } from '@vlabs/pages/handlers/store';

import st from './CreateHandlerPage.module.sass';
import {
  biometricIdentification,
  getIdentificationFaceQuality,
  defineAttributes,
  saveIdentified,
  saveUnique,
  templateRegistration,
} from './createHandlerPageHelpers';
import { Page, PageContent } from '@vlabs/monokit/shared/ui/page';

const CreateHandlerPage = ({
  createHandler,
  listOptions,
  isFaceQualityAvailable,
  isLivenessAvailable,
  lpVersion,
}) => {
  const { t } = useTranslation();
  const history = useHistory();
  const [modalIsOpen, setModalIsOpen] = useState(false);
  const [createdModalIsOpen, setCreatedModalIsOpen] = useState(false);
  const [handlerType, setHandlerType] = useState(undefined);
  const [createdHandlerId, setCreatedHandlerId] = useState();
  const [handlerName, setHandlerName] = useState();

  const tooltipText = t(`handlers:setup sections.face quality.tooltip.licenses.${isFaceQualityAvailable}`);

  const onCreateForm = () => {
    history.push(generatePath(routes.handlers.other));
  };

  const onOpenModal = (scenarioType) => {
    if (scenarioType === 'faceQuality' && !isFaceQualityAvailable) return;
    setHandlerType(scenarioType);
    setModalIsOpen(true);
  };

  const closeModal = () => {
    setModalIsOpen(false);
  };

  const onOpenCreatedModal = () => {
    closeModal();
    setCreatedModalIsOpen(true);
  };

  const closeCreatedModal = () => {
    setCreatedModalIsOpen(false);
    setCreatedHandlerId(undefined);
  };

  const onSubmitForm = async (values, func) => {
    try {
      setCreatedHandlerId(
        await createHandler(func({
          ...values,
          // FIXME: Investigate
          liveness: isLivenessAvailable,
          // FIXME: Investigate
          lpVersion,
        })),
      );
      setHandlerName(values?.description);
      onOpenCreatedModal();
    } catch (error) {
      return undefined;
    }
    return undefined;
  };

  return (
    <Page>
      <PageContent>
      <Modal
        appElement={document.getElementById('root')}
        isOpen={modalIsOpen}
        onRequestClose={closeModal}
        size="small"
      >
        {handlerType === 'biometricIdentification' && (
          <BiometricIdentificationForm
            listOptions={listOptions}
            onSubmit={(v) => onSubmitForm(v, biometricIdentification)}
          />
        )}
        {handlerType === 'templateRegistration' && (
          <TemplateRegistrationForm
            listOptions={listOptions}
            onSubmit={(v) => onSubmitForm(v, templateRegistration)}
          />
        )}
        {handlerType === 'saveUnique' && (
          <SaveUniqueForm
            listOptions={listOptions}
            onSubmit={(v) => onSubmitForm(v, saveUnique)}
          />
        )}
        {handlerType === 'saveIdentified' && (
          <SaveIdentifiedForm
            listOptions={listOptions}
            onSubmit={(v) => onSubmitForm(v, saveIdentified)}
          />
        )}
        {handlerType === 'defineAttributes' && (
          <DefineAttributesForm
            onSubmit={(v) => onSubmitForm(v, defineAttributes)}
          />
        )}

        {handlerType === 'faceQuality' && (
          <BiometricIdentificationFaceQualityForm
            listOptions={listOptions}
            onSubmit={(v) => onSubmitForm(v, getIdentificationFaceQuality)}
          />
        )}
      </Modal>

      <Modal
        appElement={document.getElementById('root')}
        isOpen={createdModalIsOpen}
        onRequestClose={closeCreatedModal}
        size="small"
      >
        <CreatedHandlerForm
          handlerId={createdHandlerId}
          handlerName={handlerName}
        />
      </Modal>

      <Menu isOpen title={t('handlers:select_policy_type_to_create')}>
        <MenuItem
          onClick={() => onOpenModal('templateRegistration')}
          testId="handler.templateRegistration"
        >
          <div className={st.MenuItemContainer}>
            <DetectionIcon className={st.MenuItemIcon} />
            {t('handlers:preset_scenario_1')}
          </div>
        </MenuItem>

        <MenuItem
          onClick={() => onOpenModal('biometricIdentification')}
          testId="handler.biometricIdentification"
        >
          <div className={st.MenuItemContainer}>
            <DetectionIcon className={st.MenuItemIcon} />
            {t('handlers:preset_scenario_2')}
          </div>
        </MenuItem>

        <MenuItem
          onClick={() => onOpenModal('saveIdentified')}
          testId="handler.saveIdentified"
        >
          <div className={st.MenuItemContainer}>
            <TechHardDriveIcon className={st.MenuItemIcon} />
            {t('handlers:preset_scenario_3')}
          </div>
        </MenuItem>

        <MenuItem
          onClick={() => onOpenModal('defineAttributes')}
          testId="handler.defineAttributes"
        >
          <div className={st.MenuItemContainer}>
            <UsersIcon className={st.MenuItemIcon} />
            {t('handlers:preset_scenario_4')}
          </div>
        </MenuItem>

        <MenuItem
          onClick={() => onOpenModal('saveUnique')}
          testId="handler.saveUnique"
        >
          <div className={st.MenuItemContainer}>
            <UsersIcon className={st.MenuItemIcon} />
            {t('handlers:preset_scenario_5')}
          </div>
        </MenuItem>

        <MenuItem
          disabled={!isFaceQualityAvailable}
          onClick={() => onOpenModal('faceQuality')}
          testId="handler.faceQuality"
        >
          <div className={st.MenuItemContainer}>
            <DetectionIcon className={cn(st.MenuItemIcon, {
              [st.MenuItemIcon_disabled]: !isFaceQualityAvailable,
            })}
            />
            <div className={st.MenuItemTextTooltip}>
              {t('handlers:face_quality.policy_name')}
              <Tooltip
                className="Tooltip_ui"
                data-tooltip-content={tooltipText}
                tooltipProps={{ effect: 'solid' }}
              >
                <InfoCircleIcon className={cn({
                  [st.Tooltip__Icon]: true,
                  [st.Tooltip__Icon_notAvailable]: !isFaceQualityAvailable,
                })}
                />
              </Tooltip>
            </div>
          </div>
        </MenuItem>

        <MenuItem
          onClick={onCreateForm}
          testId="handler.other"
        >
          <div className={st.MenuItemContainer}>
            <FileIcon className={st.MenuItemIcon} />
            {t('handlers:other')}
          </div>
        </MenuItem>
      </Menu>
      </PageContent>
    </Page>
  );
};

CreateHandlerPage.propTypes = {
  isFaceQualityAvailable: PropTypes.bool,
  isLivenessAvailable: PropTypes.bool,
  createHandler: PropTypes.func.isRequired,
  listOptions: PropTypes.arrayOf(PropTypes.shape({
    label: PropTypes.string,
    value: PropTypes.any,
  })),
  lpVersion: PropTypes.shape({
    major: PropTypes.number,
    minor: PropTypes.number,
    patch: PropTypes.number,
  }),
};
CreateHandlerPage.defaultProps = {
  isFaceQualityAvailable: false,
  isLivenessAvailable: false,
  listOptions: [],
  lpVersion: undefined,
};

export default connect((state) => ({
  listOptions: selectListOptions(state),
  isLivenessAvailable: selectIsAppFeatureAvailable(state, 'estimations.liveness'),
  isFaceQualityAvailable: selectIsAppFeatureAvailable(state, 'estimations.face_quality'),
  lpVersion: selectAppServices(state)?.lunaPlatform?.versionNum,
}),
  (dispatch) => ({
    createHandler: (data) => dispatch(createPreconfiguredHandler(data)),
  }))(CreateHandlerPage);
